import { Button, Label, Note, StatusBadge, statusTone, Textarea } from "@platform/ui";
import { ErrorText, errorKey, Field, useSession } from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslations } from "use-intl";
import { OverdueNote } from "../components/queue";
import {
  EMPTY_SCREENING,
  type ScreeningDraft,
  ScreeningFields,
  screeningBody,
  screeningReady,
} from "../components/screening-fields";
import { formatDateTime } from "../lib/i18n";

type Decision = "approve" | "reject" | "request_information";
type Authority = "director_in_registry" | "company_email_domain" | "director_confirmation";

const AUTHORITIES: readonly Authority[] = [
  "director_in_registry",
  "company_email_domain",
  "director_confirmation",
];

interface CaseView {
  readonly id: string;
  readonly enterprise: {
    readonly id: string;
    readonly legalName: string;
    readonly registrationNumber: string;
    readonly country: string;
    readonly verificationStatus: "submitted" | "under_review" | "verified" | "rejected";
  };
  readonly providerStatus: string | null;
  readonly providerResult: {
    readonly answer: "GREEN" | "RED" | null;
    readonly rejectLabels: readonly string[];
    readonly moderationComment: string | null;
    readonly clientComment: string | null;
  } | null;
  readonly reviewStartedAt: string | null;
  readonly assigneeOperatorId: string | null;
  readonly decision: "approved" | "rejected" | "info_requested" | null;
  readonly decisionReason: string | null;
  readonly provider: "sumsub" | "manual";
  readonly checks: {
    readonly registry: { readonly source: string; readonly reference: string; readonly checkedOn: string };
    readonly screening: { readonly lists: readonly string[]; readonly result: "clear" | "possible_match" };
    readonly authority: { readonly basis: Authority; readonly note?: string | undefined };
  } | null;
  readonly createdAt: string;
}

/** Today's date in Singapore, for the registry check's default. */
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date());

const MIN_REASON = 3;

/**
 * One verification case (ADM-2, card 114): the enterprise, Sumsub's result or, verified by hand, the reviewer's checks
 * (ADR 0026, card 130), and the decision with its reason, which the enterprise's admins read. It opens beside the
 * queue; `onDecided` lets the queue read itself again.
 */
export function VerificationCasePage({ onDecided }: { onDecided?: () => void } = {}) {
  const t = useTranslations();
  const { caseId = "" } = useParams();
  const { session, state } = useSession();
  const me = state.status === "signed_in" ? state.claims.sub : undefined;
  const [view, setView] = useState<CaseView | undefined>();
  const [decision, setDecision] = useState<Decision>("approve");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // What the reviewer checked, to approve a case verified by hand (ADR 0026).
  const [registry, setRegistry] = useState({ source: "", reference: "", checkedOn: today() });
  const [screening, setScreening] = useState<ScreeningDraft>(EMPTY_SCREENING);
  const [authority, setAuthority] = useState<{ basis: Authority | undefined; note: string }>({
    basis: undefined,
    note: "",
  });

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/verification-cases/{caseId}", {
      params: { path: { caseId } },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setView(data);
  }, [session, caseId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const assign = async () => {
    const { data, error: problem } = await session.api.POST("/v1/ops/verification-cases/{caseId}/assign", {
      params: { path: { caseId } },
      body: {},
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setView(data);
    onDecided?.();
  };

  const byHand = view?.provider === "manual";
  const checksReady =
    registry.source.trim().length >= 2 &&
    registry.reference.trim() !== "" &&
    registry.checkedOn !== "" &&
    screeningReady(screening) &&
    authority.basis !== undefined;
  const needsChecks = byHand && decision === "approve";

  const decide = async (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < MIN_REASON) return setError(t("admin.case.reasonTooShort"));
    if (needsChecks && !checksReady) return setError(t("admin.case.checksMissing"));
    setBusy(true);
    setError(undefined);
    try {
      const checks =
        needsChecks && authority.basis !== undefined
          ? {
              registry: {
                source: registry.source.trim(),
                reference: registry.reference.trim(),
                checkedOn: registry.checkedOn,
              },
              screening: screeningBody(screening),
              authority: {
                basis: authority.basis,
                ...(authority.note.trim() === "" ? {} : { note: authority.note.trim() }),
              },
            }
          : undefined;
      const { data, error: problem } = await session.api.POST("/v1/ops/verification-cases/{caseId}/decide", {
        params: { path: { caseId } },
        body: { decision, reason: reason.trim(), ...(checks === undefined ? {} : { checks }) },
      });
      if (data === undefined) return setError(t(errorKey(problem)));
      setView(data);
      onDecided?.();
    } finally {
      setBusy(false);
    }
  };

  if (view === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const result = view.providerResult;
  const ready = view.decision === null && view.enterprise.verificationStatus === "under_review";
  const answer = result?.answer === "GREEN" ? "passed" : result?.answer === "RED" ? "failed" : "noResult";
  const facts: { label: string; value: string }[] = [
    {
      label: t("admin.case.registration"),
      value: `${view.enterprise.country} ${view.enterprise.registrationNumber}`,
    },
    { label: t("admin.case.created"), value: formatDateTime(view.createdAt) },
    ...(result !== null && result.rejectLabels.length > 0
      ? [{ label: t("admin.case.labels"), value: result.rejectLabels.join(", ") }]
      : []),
    ...(result?.moderationComment != null
      ? [{ label: t("admin.case.moderationComment"), value: result.moderationComment }]
      : []),
    ...(result?.clientComment != null
      ? [{ label: t("admin.case.clientComment"), value: result.clientComment }]
      : []),
  ];
  return (
    <section
      aria-label={t("admin.case.title", { enterprise: view.enterprise.legalName })}
      className="flex flex-col overflow-hidden rounded-(--radius-card) border border-border bg-surface"
    >
      <header className="flex flex-col gap-1 border-b border-border-subtle px-5 py-4">
        <span className="text-label font-semibold text-ink-muted uppercase">
          {t(
            view.assigneeOperatorId === null
              ? "admin.case.unassigned"
              : view.assigneeOperatorId === me
                ? "admin.case.assignedToYou"
                : "admin.case.assignedToOther",
          )}
        </span>
        <h2 className="text-heading font-semibold text-ink">{view.enterprise.legalName}</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge
            label={t(`admin.status.${view.enterprise.verificationStatus}`)}
            tone={statusTone("verification", view.enterprise.verificationStatus)}
          />
          {view.provider === "manual" ? (
            <StatusBadge label={t("admin.case.byHand")} tone="neutral" />
          ) : (
            <StatusBadge
              label={`${t("admin.case.sumsubResult")}: ${t(`admin.queue.${answer}`)}`}
              tone={answer === "passed" ? "done" : answer === "failed" ? "problem" : "neutral"}
            />
          )}
        </div>
      </header>
      {view.decision === null && <OverdueNote startedAt={view.reviewStartedAt} />}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-5 py-4 text-small">
        {facts.map((fact) => (
          <div key={fact.label} className="contents">
            <dt className="text-ink-muted">{fact.label}</dt>
            <dd className="text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>
      <div className="border-t border-border-subtle px-5 py-4">
        {view.decision !== null ? (
          <Note
            tone={
              view.decision === "approved" ? "done" : view.decision === "rejected" ? "problem" : "attention"
            }
          >
            <p className="font-medium">
              {t("admin.case.decided", { decision: t(`admin.case.${view.decision}`) })}
            </p>
            {view.decisionReason !== null && <p>{view.decisionReason}</p>}
            {view.checks !== null && (
              <p>
                {t("admin.case.recorded", {
                  registry: `${view.checks.registry.source} ${view.checks.registry.reference} (${view.checks.registry.checkedOn})`,
                  screening: t(`admin.screening.${view.checks.screening.result}`),
                  authority: t(`admin.case.authority.${view.checks.authority.basis}`),
                })}
              </p>
            )}
          </Note>
        ) : !ready ? (
          <Note>{t("admin.case.notReady")}</Note>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={(event) => void decide(event)}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-subheading font-semibold text-ink">{t("admin.case.decisionTitle")}</h3>
              {view.assigneeOperatorId !== me && (
                <Button type="button" variant="ghost" size="sm" onClick={() => void assign()}>
                  {t("admin.case.assignToMe")}
                </Button>
              )}
            </div>
            <fieldset className="grid grid-cols-3 gap-2">
              <legend className="sr-only">{t("admin.case.decisionTitle")}</legend>
              {(["approve", "reject", "request_information"] as const).map((option) => (
                <label
                  key={option}
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-(--radius-control) border border-border px-2 py-2 text-small font-medium text-ink transition-colors duration-(--duration-fast) hover:bg-subtle has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50 has-[:checked]:text-brand-900"
                >
                  <input
                    type="radio"
                    name="decision"
                    value={option}
                    className="sr-only"
                    checked={decision === option}
                    onChange={() => setDecision(option)}
                  />
                  {t(
                    option === "request_information"
                      ? "admin.case.requestInformation"
                      : `admin.case.${option}`,
                  )}
                </label>
              ))}
            </fieldset>
            {needsChecks && (
              <div className="flex flex-col gap-4 rounded-(--radius-control) border border-border-subtle p-4">
                <h4 className="text-small font-semibold text-ink">{t("admin.case.checksTitle")}</h4>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field
                    id="registry-source"
                    label={t("admin.case.registrySource")}
                    hint={t("admin.case.registrySourceHint")}
                    value={registry.source}
                    onChange={(event) => setRegistry({ ...registry, source: event.target.value })}
                  />
                  <Field
                    id="registry-reference"
                    label={t("admin.case.registryReference")}
                    value={registry.reference}
                    onChange={(event) => setRegistry({ ...registry, reference: event.target.value })}
                  />
                  <Field
                    id="registry-checked-on"
                    type="date"
                    label={t("admin.case.registryCheckedOn")}
                    value={registry.checkedOn}
                    onChange={(event) => setRegistry({ ...registry, checkedOn: event.target.value })}
                  />
                </div>
                <ScreeningFields id="case-screening" value={screening} onChange={setScreening} />
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-small font-medium text-ink">
                    {t("admin.case.authorityTitle")}
                  </legend>
                  <div className="flex flex-col gap-2 text-small text-ink">
                    {AUTHORITIES.map((basis) => (
                      <label key={basis} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="authority"
                          className="size-4 accent-brand-600"
                          checked={authority.basis === basis}
                          onChange={() => setAuthority({ ...authority, basis })}
                        />
                        {t(`admin.case.authority.${basis}`)}
                      </label>
                    ))}
                  </div>
                  <Field
                    id="authority-note"
                    label={t("admin.case.authorityNote")}
                    value={authority.note}
                    onChange={(event) => setAuthority({ ...authority, note: event.target.value })}
                  />
                </fieldset>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="decision-reason">{t("admin.case.reason")}</Label>
              <Textarea
                id="decision-reason"
                rows={4}
                aria-describedby="decision-reason-hint"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <p id="decision-reason-hint" className="text-caption text-ink-muted">
                {t("admin.case.reasonHint", { min: MIN_REASON })}
              </p>
            </div>
            {error !== undefined && <ErrorText>{error}</ErrorText>}
            <Button
              type="submit"
              disabled={busy || reason.trim().length < MIN_REASON || (needsChecks && !checksReady)}
            >
              {t("admin.case.record")}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
